import { useState } from "react";

import { Copy, History, Pencil, Trash2 } from "lucide-react";

import { ErrorAlert } from "../../components/ErrorAlert";
import { Link } from "../../components/Link";
import { GuideCard } from "../../guide/GuideCard";
import { CANVASES_ITEMS, LESSON_ITEMS } from "../../guide/guideSteps";
import { canvasPath, revisionsPath } from "../../router";

import type { ApiErrorCode } from "../../api/client";
import type { CanvasRecord } from "@canvaschool/contracts";

export const CanvasesPanel = ({
  classId,
  canvases,
  canManage,
  isTeacher,
  isStudent,
  guideUserId,
  onOpen,
  catalogNotice,
  onRetryCatalog,
  onCreate,
  onRename,
  onTrash,
  onDuplicate,
}: {
  classId: string;
  canvases: CanvasRecord[];
  canManage: boolean;
  isTeacher: boolean;
  isStudent: boolean;
  /** Teachers see the how-to card; omitted for anyone else. */
  guideUserId?: string;
  /** Called when a canvas link is followed. */
  onOpen?: () => void;
  catalogNotice: ApiErrorCode | null;
  onRetryCatalog: () => void;
  onCreate: () => void;
  onRename: (canvas: CanvasRecord, title: string) => void;
  onTrash: (canvas: CanvasRecord) => void;
  onDuplicate: (canvas: CanvasRecord) => void;
}) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draftTitle, setDraftTitle] = useState("");

  const startEditing = (canvas: CanvasRecord) => {
    setEditingId(canvas.id);
    setDraftTitle(canvas.title);
  };

  const commitEditing = (canvas: CanvasRecord) => {
    setEditingId(null);
    const trimmed = draftTitle.trim();
    if (trimmed && trimmed !== canvas.title) {
      onRename(canvas, trimmed);
    }
  };

  return (
    <section
      aria-labelledby="canvaschool-canvases-heading"
      className="canvaschool-no-print"
    >
      {isStudent && (
        <p className="canvaschool-student-lead">
          열고 싶은 캔버스를 골라 주세요.
        </p>
      )}
      <div
        className={`canvaschool-title-row${
          isStudent ? " canvaschool-title-row--student" : ""
        }`}
      >
        <h2
          id="canvaschool-canvases-heading"
          className={isStudent ? "canvaschool-visually-hidden" : undefined}
        >
          캔버스
        </h2>
        {canManage && (
          <button
            type="button"
            className="canvaschool-btn-primary"
            onClick={onCreate}
          >
            캔버스 만들기
          </button>
        )}
      </div>
      {guideUserId && (
        <GuideCard
          id="canvases"
          title="캔버스로 수업하기"
          userId={guideUserId}
          details={[...CANVASES_ITEMS, ...LESSON_ITEMS]}
        >
          템플릿을 골라 만들면 같은 학급 학생 모두가 열 수 있습니다.
        </GuideCard>
      )}
      {catalogNotice && (
        <>
          <ErrorAlert code={catalogNotice} />
          <button type="button" onClick={onRetryCatalog}>
            다시 시도
          </button>
        </>
      )}
      {canvases.length === 0 ? (
        guideUserId && canManage ? (
          <div className="canvaschool-empty">
            <p className="canvaschool-empty-title">
              첫 캔버스를 만들어 보세요.
            </p>
            <p className="canvaschool-empty-text">
              템플릿을 고르면 학생 수에 맞춰 프레임이 준비됩니다.
            </p>
            <button
              type="button"
              className="canvaschool-btn-primary"
              onClick={onCreate}
            >
              캔버스 만들기
            </button>
          </div>
        ) : (
          <p>아직 캔버스가 없습니다.</p>
        )
      ) : (
        <ul
          className={`canvaschool-list canvaschool-canvas-list${
            isStudent ? " canvaschool-canvas-list--student" : ""
          }`}
        >
          {canvases.map((canvas) => (
            <li key={canvas.id} className="canvaschool-canvas-item">
              <Link
                className="canvaschool-canvas-item-link"
                to={canvasPath(classId, canvas.id)}
                aria-label={canvas.title}
                onClick={() => onOpen?.()}
              />
              <div className="canvaschool-canvas-item-body">
                <div className="canvaschool-canvas-title-row">
                  {editingId === canvas.id ? (
                    <input
                      className="canvaschool-canvas-title-input"
                      autoFocus
                      value={draftTitle}
                      aria-label={`${canvas.title} 제목 수정`}
                      onChange={(event) => setDraftTitle(event.target.value)}
                      onBlur={() => commitEditing(canvas)}
                      onKeyDown={(event) => {
                        if (event.key === "Enter") {
                          event.preventDefault();
                          commitEditing(canvas);
                        }
                        if (event.key === "Escape") {
                          event.preventDefault();
                          setEditingId(null);
                        }
                      }}
                    />
                  ) : (
                    <>
                      <h2 className="canvaschool-canvas-title">
                        {canvas.title}
                      </h2>
                      {canvas.kind === "PRESENTATION" && (
                        <span className="canvaschool-badge canvaschool-badge--presentation">
                          {isStudent ? "발표" : "발표 수업"}
                        </span>
                      )}
                      {canManage && (
                        <button
                          type="button"
                          className="canvaschool-icon-btn"
                          aria-label={`${canvas.title} 이름 변경`}
                          onClick={() => startEditing(canvas)}
                        >
                          <Pencil size={20} aria-hidden="true" />
                        </button>
                      )}
                    </>
                  )}
                </div>
                <div className="canvaschool-actions canvaschool-actions--icons">
                  {/* A presentation lesson draws nothing itself; its
                      students' slides have their own history. */}
                  {isTeacher && canvas.kind !== "PRESENTATION" && (
                    <Link
                      className="canvaschool-icon-btn"
                      to={revisionsPath(classId, canvas.id)}
                      aria-label={`${canvas.title} 이력`}
                    >
                      <History size={20} aria-hidden="true" />
                    </Link>
                  )}
                  {canManage && canvas.kind !== "PRESENTATION" && (
                    <button
                      type="button"
                      className="canvaschool-icon-btn"
                      aria-label={`${canvas.title} 복제`}
                      onClick={() => onDuplicate(canvas)}
                    >
                      <Copy size={20} aria-hidden="true" />
                    </button>
                  )}
                  {canManage && (
                    <button
                      type="button"
                      className="canvaschool-icon-btn canvaschool-icon-btn--danger"
                      aria-label={`${canvas.title} 휴지통으로 이동`}
                      onClick={() => onTrash(canvas)}
                    >
                      <Trash2 size={20} aria-hidden="true" />
                    </button>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
      {isStudent && (
        <p className="canvaschool-hint canvaschool-student-hint">
          캔버스가 보이지 않으면 선생님께 알려 주세요.
        </p>
      )}
    </section>
  );
};
